import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useChat } from "../lib/chatContext";
import { useTour } from "../lib/useTour";
import { subscribeConnections, subscribeIncomingPasses } from "../lib/queries";
import { MobileTabBar } from "./MobileTabBar";
import { ThemeToggle } from "./ThemeToggle";
import { DisplayControls } from "./DisplayControls";

type Active =
  | "kitchen"
  | "ondeck"
  | "shopping"
  | "leaderboard"
  | "served"
  | "mcp"
  | "prefs"
  | null;

export function AppBar({ active = null, tag }: { active?: Active; tag?: string }) {
  const { signOut, user } = useAuth();
  const chat = useChat();
  const tour = useTour();
  const tagline = tag ?? "the slow refinement of every recipe.";

  // Live badge: recipes served to me still waiting + incoming friend requests.
  const [serveCount, setServeCount] = useState(0);
  const [reqCount, setReqCount] = useState(0);
  const uid = user?.uid;
  useEffect(() => {
    if (!uid) {
      setServeCount(0);
      setReqCount(0);
      return;
    }
    const unsubServes = subscribeIncomingPasses(uid, (sends) =>
      setServeCount(sends.filter((s) => s.status === "pending").length)
    );
    const unsubConns = subscribeConnections(uid, (conns) =>
      setReqCount(
        conns.filter((c) => c.status === "pending" && c.requestedBy !== uid).length
      )
    );
    return () => {
      unsubServes();
      unsubConns();
    };
  }, [uid]);
  const passCount = serveCount + reqCount;
  return (
    <>
    <header className="appbar">
      <div className="appbar-brand">
        <Link to="/">
          <span className="name">Affinage</span>
          <span className="tagline">{tagline}</span>
        </Link>
      </div>
      <nav className="appbar-nav">
        <NavLink
          to="/"
          end
          className={active === "kitchen" ? "active" : undefined}
          data-tour-id="nav-cookbook"
        >
          My Cookbook
        </NavLink>
        <NavLink
          to="/on-deck"
          className={active === "ondeck" ? "active" : undefined}
          data-tour-id="nav-ondeck"
        >
          On Deck
        </NavLink>
        <NavLink
          to="/shopping"
          className={active === "shopping" ? "active" : undefined}
          data-tour-id="nav-shopping"
        >
          Shopping
        </NavLink>
        <NavLink
          to="/community"
          className={active === "leaderboard" ? "active" : undefined}
          data-tour-id="nav-community"
        >
          The Kitchen Table
        </NavLink>
        <NavLink
          to="/served"
          className={active === "served" ? "active" : undefined}
          data-tour-id="nav-served"
        >
          Served to You
          {passCount > 0 && <span className="nav-badge">{passCount}</span>}
        </NavLink>
        <button
          className="chat-trigger"
          onClick={() => chat.open({ mode: "create" })}
          type="button"
        >
          <span className="spark">✨</span>Generate
        </button>
        <NavLink
          to="/connect"
          className={active === "mcp" ? "active" : undefined}
          data-tour-id="nav-assistant"
        >
          <span className="spark">⚡</span>Assistant
        </NavLink>
        <NavLink
          to="/preferences"
          className={active === "prefs" ? "active" : undefined}
          data-tour-id="nav-preferences"
        >
          Preferences
        </NavLink>
        {user && (
          <button
            type="button"
            className="appbar-tour-trigger"
            onClick={() => tour.start()}
            title="Replay the tour"
          >
            Tour
          </button>
        )}
        {user && (
          <button type="button" onClick={() => tour.startIntro()} title="Replay the opening titles">
            Intro
          </button>
        )}
        <DisplayControls />
        <ThemeToggle />
        {user && <button onClick={() => signOut()}>Sign Out</button>}
      </nav>
      {/* Phones hide .appbar-nav, so surface the two always-available controls
          in the top bar's trailing slot (desktop keeps the ones in the nav). */}
      <div className="appbar-mobile-actions">
        <DisplayControls className="display-controls-mobile" />
        <ThemeToggle className="appbar-toggle-mobile" />
      </div>
    </header>
    <MobileTabBar
      passCount={passCount}
      signedIn={!!user}
      onGenerate={() => chat.open({ mode: "create" })}
      onTour={() => tour.start()}
      onIntro={() => tour.startIntro()}
      onSignOut={() => signOut()}
    />
    </>
  );
}
